<template>
  <v-container
    fluid
    class="fill-height"
  >
    <v-row
      class="ma-0"
    >
      <v-subheader class="pa-0">
        <span class="title">Informações do <b>titular do boleto</b></span>
      </v-subheader>
      <v-card
        width="100%"
        :loading="isLoading"
      >
        <v-skeleton-loader
          v-if="isLoading"
          :loading="isLoading"
          type="article"
        />
        <v-container
          v-else
          fluid
          class="fill-height"
        >
          <v-row
            class="ma-0"
          >
            <v-col
              cols="12"
              sm="12"
              md="4"
              lg="4"
              xl="4"
            >
              <v-text-field
                v-model="strName"
                label="Nome completo"
                outlined
                :error-messages="strNameErrors"
              />
            </v-col>
            <v-col
              cols="12"
              sm="12"
              md="4"
              lg="4"
              xl="4"
            >
              <v-text-field
                v-model="strEmail"
                label="E-mail"
                outlined
                :error-messages="strEmailErrors"
              />
            </v-col>
            <v-col
              cols="12"
              sm="12"
              md="4"
              lg="4"
              xl="4"
            >
              <v-text-field
                v-model="strPhoneNumber"
                label="Telefone"
                v-mask="strPhoneNumberMasks"
                outlined
                :error-messages="strPhoneNumberErrors"
              />
            </v-col>
            <v-col
              cols="12"
              sm="12"
              md="4"
              lg="4"
              xl="4"
            >
              <v-text-field
                v-model="dtBirthDate"
                label="Data de nascimento"
                v-mask="dtBirthDateMasks"
                outlined
                :error-messages="dtBirthDateErrors"
              />
            </v-col>
            <v-col
              cols="12"
              sm="12"
              md="4"
              lg="4"
              xl="4"
            >
              <v-text-field
                v-model="strDocumentNumber"
                label="CPF/CNPJ"
                v-mask="strDocumentNumberMasks"
                outlined
                :error-messages="strDocumentNumberErrors"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-row>
    <v-row
      class="ma-0 my-2"
    >
      <v-divider/>
    </v-row>
    <v-row
      class="ma-0"
    >
      <v-btn
        :disabled="isRequesting"
        color="accent"
        outlined
        @click="this.LastStep"
        :loading="PaymentMixin.isPaying"
      >
        Voltar
      </v-btn>
      <v-spacer/>
      <v-btn
        color="primary"
        @click="onNextClick"
        :loading="isRequesting || PaymentMixin.isPaying"
      >
        Próximo
      </v-btn>
    </v-row>
  </v-container>
</template>

<script>
  import {validationMixin} from "vuelidate"
  import required from "vuelidate/lib/validators/required"
  import {isEmpty, lt, lte} from "lodash/lang"
  import {last} from "lodash/array"
  import {forEach} from "lodash/collection"
  import email from "vuelidate/lib/validators/email"
  import {documentNumberValidation, phoneNumberValidation, unmask} from "../../../helpers/utils"
  import minLength from "vuelidate/lib/validators/minLength"
  import moment from "moment"
  import {inRange} from "lodash/number"
  import {DOCUMENT_TYPE} from "../../../helpers/payment/constants"
  import {createPayerInfo, listPayerInfo, updatePayerInfo} from "../../../api/api-billing"
  import {NOTIFICATION_TYPES, NotificationOptions} from "../../../helpers/notify"

  const PaymentLastStep = Symbol.for("PaymentLastStep")
  const PaymentNextStep = Symbol.for("PaymentNextStep")
  const SetResumePayerInfo = Symbol.for("SetResumePayerInfo")
  const SetCdPayerInfo = Symbol.for("SetCdPayerInfo")
  const _PaymentMixin = Symbol.for("_PaymentMixin")

  export default {
    name: "PostPayPayerInfo",
    mixins: [validationMixin],
    inject: {LastStep: PaymentLastStep, NextStep: PaymentNextStep, SetResumePayerInfo, _PaymentMixin, SetCdPayerInfo},
    data: () => ({
      isLoading: false,

      isRequesting: false,

      idPayerInfo: null,

      strName: null,
      strEmail: null,
      strPhoneNumber: null,
      dtBirthDate: null,
      strDocumentNumber: null,
    }),
    validations() {
      return {
        strName: {
          required,
          isValid: function () {
            if (!this.strName) return false
            const strNameSplit = this.strName.split(" ")

            if (lt(strNameSplit.length, 2)) return false

            let isValid = true
            forEach(strNameSplit, str => {
              if (lt(str.length, 1)) {
                isValid = false
              }
            })
            return isValid
          }
        },
        strEmail: {
          required,
          email
        },
        strPhoneNumber: {
          required,
          isValid: function () {
            const unmaskedPhoneNumber = unmask(this.strPhoneNumber, this.strPhoneNumberMasks)
            const prefix = unmaskedPhoneNumber.substr(0, 2)
            const number = unmaskedPhoneNumber.substr(2)
            return phoneNumberValidation(prefix, number)
          }
        },
        dtBirthDate: {
          required,
          minLength: minLength(10),
          isValid: function () {
            if (!this.dtBirthDate) return false
            if (lt(this.dtBirthDate.length, 10)) return false

            const dtBirthDateSplit = this.dtBirthDate.split("/")
            const [day, month, year] = dtBirthDateSplit

            const currAge = moment(moment.now()).year() - Number(year)

            if (!inRange(Number(day), 1, 32)) return false
            else if (!inRange(Number(month), 1, 13)) return false
            else if (!inRange(Number(currAge), 18, 201)) return false

            return true
          }
        },
        strDocumentNumber: {
          required,
          isValid: function () {
            const unmaskedDocumentNumber = unmask(this.strDocumentNumber, this.strDocumentNumberMasks)
            return documentNumberValidation(unmaskedDocumentNumber)
          }
        },
      }
    },
    methods: {
      onNextClick() {
        this.$v.$touch()
        if (this.$v.$invalid) return

        this.isRequesting = true
        if (!this.idPayerInfo) {
          this.createPayerInfo()
            .then(() => {
              const resumePayerInfo = {
                strName: this.strName,
                strEmail: this.strEmail,
                strDocumentNumber: this.strDocumentNumber
              }
              this.SetResumePayerInfo(resumePayerInfo)
              this.NextStep()
            })
        } else {
          this.updatePayerInfo()
            .then(() => {
              const resumePayerInfo = {
                strName: this.strName,
                strEmail: this.strEmail,
                strDocumentNumber: this.strDocumentNumber
              }
              this.SetResumePayerInfo(resumePayerInfo)
              this.NextStep()
            })
        }
      },
      async createPayerInfo() {
        const payerInfoData = {
          str_name: this.strName,
          str_email: this.strEmail,
          str_phone_number: `+55${unmask(this.strPhoneNumber, this.strPhoneNumberMasks)}`,
          dt_birth_date: moment(this.dtBirthDate, "DD/MM/YYYY").format("YYYY-MM-DD"),
          str_document_number: unmask(this.strDocumentNumber, this.strDocumentNumberMasks),
          ab_document_type: this.abDocumentType
        }
        return createPayerInfo(this.$apiAuth.getAuthToken(), payerInfoData)
          .then(res => {
            const payerInfo = res.data.data
            this.idPayerInfo = payerInfo["id_payer_info"]
            this.SetCdPayerInfo(payerInfo["id_payer_info"])
          })
          .catch(() => {
            this.$notify(new NotificationOptions({
              message: "Ocorreu algum erro ao atualizar suas informações de pagador, tente novamente mais tarde.",
              type: NOTIFICATION_TYPES.ERROR
            }))
          })
          .finally(() => {
            this.isRequesting = false
          })
      },
      async updatePayerInfo() {
        const payerInfoData = {
          str_name: this.strName,
          str_email: this.strEmail,
          str_phone_number: `+55${unmask(this.strPhoneNumber, this.strPhoneNumberMasks)}`,
          dt_birth_date: moment(this.dtBirthDate, "DD/MM/YYYY").format("YYYY-MM-DD"),
          str_document_number: unmask(this.strDocumentNumber, this.strDocumentNumberMasks),
          ab_document_type: this.abDocumentType
        }
        return updatePayerInfo(this.$apiAuth.getAuthToken(), this.idPayerInfo, payerInfoData)
          .then(res => {
            const payerInfo = res.data.data
            this.idPayerInfo = payerInfo["id_payer_info"]
            this.SetCdPayerInfo(payerInfo["id_payer_info"])
          })
          .catch(() => {
            this.$notify(new NotificationOptions({
              message: "Ocorreu algum erro ao atualizar suas informações de pagador, tente novamente mais tarde.",
              type: NOTIFICATION_TYPES.ERROR
            }))
          })
          .finally(() => {
            this.isRequesting = false
          })
      }
    },
    computed: {
      PaymentMixin() {
        return this._PaymentMixin()
      },
      strNameErrors() {
        const errors = []
        if (!this.$v.strName.$dirty) return errors
        !this.$v.strName.required && errors.push("Esse campo é obrigatório")
        !this.$v.strName.isValid && errors.push("É necessário preencher com nome e sobrenome válidos")
        return errors
      },
      strEmailErrors() {
        const errors = []
        if (!this.$v.strEmail.$dirty) return errors
        !this.$v.strEmail.required && errors.push("Esse campo é obrigatório")
        !this.$v.strEmail.email && errors.push("E-mail inválido")
        return errors
      },
      strPhoneNumberMasks() {
        return ["(##) #####-####", "(##) ####-####"]
      },
      strPhoneNumberErrors() {
        const errors = []
        if (!this.$v.strPhoneNumber.$dirty) return errors
        !this.$v.strPhoneNumber.required && errors.push("Esse campo é obrigatório")
        !this.$v.strPhoneNumber.isValid && errors.push("Número de telefone inválido")
        return errors
      },
      dtBirthDateMasks() {
        return ["##/##/####"]
      },
      dtBirthDateErrors() {
        const errors = []
        if (!this.$v.dtBirthDate.$dirty) return errors
        !this.$v.dtBirthDate.required && errors.push("Esse campo é obrigatório")
        !this.$v.dtBirthDate.minLength && errors.push("Data inválida")
        !this.$v.dtBirthDate.isValid && errors.push("Data inválida")
        return errors
      },
      strDocumentNumberMasks() {
        return ["###.###.###-##", "##.###.###/####-##"]
      },
      strDocumentNumberErrors() {
        const errors = []
        if (!this.$v.strDocumentNumber.$dirty) return errors
        !this.$v.strDocumentNumber.required && errors.push("Esse campo é obrigatório")
        !this.$v.strDocumentNumber.isValid && errors.push("CPF/CNPJ inválido")
        return errors
      },
      abDocumentType() {
        if (!this.strDocumentNumber) return DOCUMENT_TYPE.CPF

        const unmaskedDocumentNumber = unmask(this.strDocumentNumber, this.strDocumentNumberMasks)
        if (lte(unmaskedDocumentNumber.length, 11)) return DOCUMENT_TYPE.CPF
        return DOCUMENT_TYPE.CNPJ
      }
    },
    mounted() {
      this.isLoading = true
      listPayerInfo(this.$apiAuth.getAuthToken())
        .then(res => {
          const listedPayerInfo = res.data.data
          if (!isEmpty(listedPayerInfo)) {
            const payerInfo = last(listedPayerInfo)
            this.idPayerInfo = payerInfo["id_payer_info"]
            this.strName = payerInfo["str_name"]
            this.strEmail = payerInfo["str_email"]
            this.strPhoneNumber = payerInfo["str_phone_number"].substr(3)
            this.dtBirthDate = moment(payerInfo["dt_birth_date"]).format("DD/MM/YYYY")
            this.strDocumentNumber = payerInfo["str_document_number"]
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
</script>

<style scoped>

</style>
